<template>
  <b-card>
    <!-- logo & banners -->
    <b-row>
      <!-- upload logo -->
      <b-col md="6">
        <b-media no-body>
          <b-media-aside>
            <b-link v-if="logoImg">
              <b-img
                rounded
                thumbnail
                :src="logoImg"
                :class="{ 'cursor-not-allowed': isNotEntityAdmin }"
              />
            </b-link>
            <b-link v-else>
              <div
                class="rounded img-thumbnail"
                :class="{ 'cursor-not-allowed': isNotEntityAdmin }"
              >
                <feather-icon
                  v-if="!logoImg"
                  icon="UploadCloudIcon"
                  size="30"
                  :class="{ 'position-absolute': true, 'img-btn-disabled': isNotEntityAdmin }"
                  :disabled="isNotEntityAdmin"
                  @click="$refs.refInputEl.$el.click()"
                />
              </div>
            </b-link>
            <!--/ avatar -->
          </b-media-aside>

          <b-media-body class="mt-75 ml-75">
            <!-- upload button -->
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              size="sm"
              class="mb-75 mr-75"
              :class="{ 'cursor-not-allowed': isNotEntityAdmin }"
              :disabled="isNotEntityAdmin"
              @click="$refs.refInputEl.$el.click()"
            >
              Upload logo
            </b-button>
            <b-form-file
              ref="refInputEl"
              v-model="uncroppedLogoFile"
              accept=".jpg, .png, .gif"
              :hidden="true"
              plain
            />
            <!--/ upload button -->

            <!-- reset -->
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              size="sm"
              class="mb-75 mr-75"
              :class="{ 'cursor-not-allowed': isNotEntityAdmin }"
              :disabled="isNotEntityAdmin"
              @click.stop="
                logoFile = null;
                logoImg = null;
                resetLogo();
              "
            >
              Reset logo
            </b-button>
            <!--/ reset -->
            <b-card-text>Upload your logo here. Allowed JPG, GIF or PNG. Max size of
              5MB</b-card-text>
          </b-media-body>
        </b-media>
      </b-col>
      <!--/ upload logo -->
      <!-- upload banners -->
      <b-col md="6">
        <b-media no-body>
          <b-media-aside :class="{ 'cursor-not-allowed': isNotEntityAdmin }">
            <b-link :class="{ 'cursor-not-allowed': !bannerImgs.length && isNotEntityAdmin }">
              <template v-if="bannerImgs.length">
                <b-carousel
                  id="carousel-interval"
                  controls
                  indicators
                  fade
                  :interval="3000"
                  :class="{'carousel': true, 'cursor-not-allowed': isNotEntityAdmin}"
                >
                  <b-carousel-slide
                    v-for="img in bannerImgs"
                    :key="img"
                  >
                    <template #img>
                      <b-img
                        thumbnail
                        rounded
                        :src="`${img}?t=${Date.now()}`"
                        :class="{'thumbnail-image': true, 'cursor-not-allowed': isNotEntityAdmin}"
                      />
                    </template>
                  </b-carousel-slide>
                </b-carousel>
              </template>
              <template v-else>
                <b-img
                  rounded
                  thumbnail
                  :class="{ 'cursor-not-allowed': isNotEntityAdmin }"
                />
                <feather-icon
                  icon="UploadCloudIcon"
                  size="30"
                  class="position-absolute"
                  :class="{ 'img-btn-disabled': isNotEntityAdmin }"
                  :disabled="isNotEntityAdmin"
                  @click="$bvModal.show('modal-select')"
                />
              </template>
            </b-link>
            <!--/ avatar -->
          </b-media-aside>

          <b-media-body class="mt-75 ml-75">
            <!-- upload button -->
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              v-b-modal.modal-select
              variant="primary"
              size="sm"
              class="mb-75 mr-75"
              :class="{ 'cursor-not-allowed': isNotEntityAdmin }"
              :disabled="isNotEntityAdmin"
            >
              Upload banners
            </b-button>
            <!--/ upload button -->

            <!-- reset -->
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              size="sm"
              class="mb-75 mr-75"
              :class="{ 'cursor-not-allowed': isNotEntityAdmin }"
              :disabled="isNotEntityAdmin"
              @click.stop="
                bannerImgs = [];
                resetBanners();
              "
            >
              Reset banners
            </b-button>
            <!--/ reset -->
            <b-card-text>Upload up to 3 banners here. Allowed JPG, GIF or PNG. Max size of
              5MB each.</b-card-text>
          </b-media-body>
        </b-media>
      </b-col>
      <!--/ upload banners -->
    </b-row>
    <!-- form -->
    <b-form
      class="mt-2"
      @submit.prevent="saveGeneralSettings"
    >
      <b-row>
        <!-- entity name -->
        <b-col sm="6">
          <b-form-group
            :label="entityName"
            label-for="account-entityName"
          >
            <b-form-input
              v-model="generalLocal.entityName"
              name="entityName"
              :class="{'disabled-input': isNotEntityAdmin}"
              :placeholder="`Enter ${entityName}`"
              :disabled="isNotEntityAdmin"
            />
          </b-form-group>
        </b-col>
        <!-- position -->
        <b-col sm="6">
          <b-form-group
            label="Position"
            label-for="account-position"
          >
            <b-form-input
              :value="USER_ROLES_LABEL[generalLocal.role]"
              name="account-position"
              placeholder="Position"
              :class="{'disabled-input': isNotEntityAdmin}"
              disabled
            />
          </b-form-group>
        </b-col>
        <!-- first name -->
        <b-col sm="6">
          <b-form-group
            label="First Name"
            label-for="account-firstname"
          >
            <b-form-input
              v-model="generalLocal.firstName"
              name="firstName"
              placeholder="Enter First Name"
            />
          </b-form-group>
        </b-col>
        <!-- last name -->
        <b-col sm="6">
          <b-form-group
            label="Last Name"
            label-for="account-lastname"
          >
            <b-form-input
              v-model="generalLocal.lastName"
              name="lastName"
              placeholder="Enter Last Name"
            />
          </b-form-group>
        </b-col>
        <!-- country -->
        <b-col sm="6">
          <b-form-group
            label="Country"
            label-for="account-country"
          >
            <v-select
              id="account-country"
              v-model="generalLocal.country"
              name="country"
              :options="commonData.COUNTRIES"
              placeholder="Enter Country"
              :disabled="isNotEntityAdmin"
            />
          </b-form-group>
        </b-col>
        <!-- phone -->
        <b-col sm="6">
          <b-form-group
            label="Phone"
            label-for="account-phone"
          >
            <vue-tel-input
              :value="generalLocal.phone ? fullPhoneNumber : ''"
              :input-options="inputOptions"
              :style-classes="{ 'form-control': true, 'tel-input-disabled': isNotEntityAdmin }"
              mode="international"
              name="phone"
              :disabled="isNotEntityAdmin"
              @input="onInputPhoneNumber"
            />
            <small
              v-if="isNotValidPhoneNumber"
              class="text-danger"
            >Please enter the valid phone number</small>
          </b-form-group>
        </b-col>
        <b-col
          v-if="isRetailer"
          sm="6"
        >
          <b-form-group
            label="Store Type"
            label-for="store-type"
          >
            <v-select
              id="store-type"
              v-model="generalLocal.storeType"
              name="country"
              :options="Object.values(STORE_TYPE)"
              :reduce="(storeType) => storeType.LABEL"
              label="LABEL"
              class="text-capitalize"
              placeholder="Enter Store Type"
              :disabled="isNotEntityAdmin"
            />
          </b-form-group>
        </b-col>
        <!-- about -->
        <b-col sm="12">
          <b-form-group
            label="About"
            label-for="account-about"
          >
            <b-form-textarea
              v-model="generalLocal.storeDescription"
              name="storeDescription"
              placeholder="Enter About"
              :class="{'disabled-input': isNotEntityAdmin}"
              :disabled="isNotEntityAdmin"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mt-2 mr-1"
            :disabled="saving"
            @click="saveGeneralSettings"
          >
            Save changes
            <feather-icon
              v-if="saving"
              icon="LoaderIcon"
            />
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            type="reset"
            class="mt-2"
            :class="{ 'cursor-not-allowed': isNotEntityAdmin }"
            @click.prevent="resetForm"
          >
            Reset
          </b-button>
        </b-col>
      </b-row>
    </b-form>
    <b-modal
      id="modal-select"
      size="lg"
      modal-class="modal-primary"
      centered
      title="Upload Banner"
      hide-footer
      header-class="custom-modal-heading"
    >
      <banner-cropper
        :profile-general="generalLocal"
        @on-cancel="closePopup"
        @on-save="updateBanners"
      />
    </b-modal>
    <b-modal
      id="croplogophoto"
      ref="croplogophoto"
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      centered
      size="lg"
    >
      <div class="pb-1 mb-2 border-bottom">
        <span class="custom-modal-heading">Crop Logo </span>
        <feather-icon
          class="float-right close-x-icon"
          icon="XIcon"
          size="24"
          @click="closeCrop"
        />
      </div>
      <div class="cropper-section mb-1">
        <Cropper
          ref="logoCropper"
          class="cropper"
          :src="croppedImg"
          :stencil-props="{
            aspectRatio: 1 / 1
          }"
          image-restriction="fit-area"
        />
        <div class="text-center mt-1">
          <b-button
            class="mr-1 font-weight-bolder"
            variant="outline-info"
            @click="closeCrop"
          >
            Cancel
          </b-button>
          <b-button
            class="ml-1"
            variant="info"
            :disabled="isCropping"
            @click="crop"
          >
            {{ isCropping ? "Saving...." : "Save" }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BFormFile,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BRow,
  BCol,
  BCard,
  BCardText,
  BMedia,
  BMediaAside,
  BMediaBody,
  BLink,
  BImg,
  BModal,
  BCarousel,
  BCarouselSlide,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { toBase64 } from '@/common-utils'
import { Cropper } from 'vue-advanced-cropper'
import { useProfileGeneral } from '@/composables/useProfileGeneral'
import BannerCropper from './banner-cropper/BannerCropper.vue'

export default {
  components: {
    BButton,
    BForm,
    BImg,
    BFormFile,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
    BCol,
    BCard,
    BCardText,
    BMedia,
    BMediaAside,
    BMediaBody,
    BLink,
    BCarousel,
    BCarouselSlide,
    BModal,
    BannerCropper,
    vSelect,
    Cropper,
  },
  directives: {
    Ripple,
  },
  props: {
    generalData: {
      type: Object,
      default: () => { },
    },
    commonData: {
      type: Object,
      default: () => { },
    },
  },
  setup(props) {
    const gen = useProfileGeneral()
    gen.initGeneral(props.generalData)
    const {
 saveGeneralSettings: _saveGeneralSettings, cropLogo: _cropLogo, resetForm: _resetForm, ...genRest
} = gen
    return {
      ...genRest,
      _saveGeneralSettings,
      _cropLogo,
      _resetForm,
    }
  },
  data() {
    return {
      inputOptions: {
        id: 'contact-number',
        placeholder: 'Enter phone number',
      },
    }
  },
  watch: {
    uncroppedLogoFile: {
      async handler(theFile) {
        if (!theFile) return
        this.croppedImg = await toBase64(theFile)
        this.$refs.croplogophoto.show()
      },
      immediate: true,
    },
  },
  created() {
    if (this.isNotEntityAdmin) {
      setTimeout(() => this.checkAndDisableTelDropdown(), 1000)
    }
  },
  emits: ['update-general'],
  methods: {
    checkAndDisableTelDropdown() {
      const telDropdown = document.getElementsByClassName('vti__dropdown')
      if (telDropdown?.length > 0) {
        telDropdown.forEach(dropdown => {
          dropdown.style.cursor = 'not-allowed'
          dropdown.parentElement.style.boxShadow = 'none'
        })
      }
    },
    closePopup() {
      this.$bvModal.hide('modal-select')
    },
    closeCrop() {
      this.uncroppedLogoFile = null
      this.$refs.croplogophoto.hide()
    },
    async crop() {
      await this._cropLogo(this.$refs.logoCropper)
      this.$refs.croplogophoto.hide()
      this.uncroppedLogoFile = null
    },
    updateBanners() {
      this.bannerImgs = this.generalLocal.banner
      const shouldResetBanners = this.bannerImgs.length === 0
      this.saveGeneralSettings(null, false, shouldResetBanners)
      this.$bvModal.hide('modal-select')
    },
    async saveGeneralSettings(event, resetLogo, resetBanner) {
      const result = await this._saveGeneralSettings(resetLogo, resetBanner)
      if (result?.success) {
        this.$emit('update-general', result.payload)
      }
    },
    resetForm() {
      this._resetForm(this.generalData)
    },
  },
}
</script>

<style scoped lang="scss">
@import 'src/@core/scss/base/components/_variables.scss';

.media-aside {
  .img-thumbnail {
    width: 80px;
    height: 80px;
    object-fit: contain;
  }

  .feather-upload-cloud {
    left: 40px;
    top: 25px;
  }
}

.carousel {
  width: 80px;
  height: 80px;
  background: $nav-component-border-color;
}

.thumbnail-image {
  object-fit: contain;
  width: 80px;
  height: 80px;
}

.disabled-input {
  cursor: not-allowed;
}

.img-btn-disabled {
  cursor: not-allowed;
  pointer-events: none;
}

.tel-input-disabled {
  background-color: #F5F5F5;
  border: 1px solid #E0E0E0 !important
}

.tel-input-disabled:focus {
  border: 1px solid #E0E0E0 !important;
  box-shadow: none !important
}

.tel-input-disabled > * {
  cursor: not-allowed !important;
}
</style>
